import React from "react";
import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { OpenInNewRounded } from "@mui/icons-material";
import { MdOutlinedFlag, MdOutlineEventSeat } from "react-icons/md";
import { List, ListItem, Stack, Typography } from "@mui/material";
import { Box, ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { MLBAdobeScriptHeader } from "@/global/common/mlb-common/MLBAdobeScriptHeader";

import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { Divider } from "@/shared/base/components/Divider/Divider";
import type { InventoryType } from "@/global/store/data/types";

export const TampaBayRaysLink = () => (
  <Link
    target="_blank"
    sx={{
      fontWeight: 500,
      color: "primary.main",
      textDecoration: "none",
    }}
    href="https://www.mlb.com/rays/tickets/my-tickets"
  >
    MyTickets
  </Link>
);

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose your games.",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Connect your MLB.com account",
    description:
      "Click Continue and connect your Tampa Bay Rays account using our secure MLB.com login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Suite",
    description: "Select the suite you wish to purchase for your game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your MLB Ballpark app or by logging into
        your account via{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="http://www.raysbaseball.com/myaccount"
        >
          http://www.raysbaseball.com/myaccount
        </Link>
        .
      </>
    ),
  },
];

export const faqs = [
  {
    title: "Is catering included in my suite or party area?",
    description: (
      <>
        <strong>
          All Suites, Daiquiri Deck, RIPA Bullpen Box & Seminole Hard Rock 162
          Landing Catering:
        </strong>{" "}
        Catering is included. Catering package includes Hot Dogs, Chicken
        Tenders, Bavarian Pretzels, Salsa & Guacamole, Bottomless Popcorn, Snack
        Attack, Salad, Cookies, Water and Coca-Cola Products. Upon completing
        your purchase, a Rays representative will reach out to connect you with
        the catering manager should you wish to upgrade your catering menu.
        <br />
        <br />
        <strong>Patio Box 1, Patio Box 2 & Patio Box 3 Catering:</strong>{" "}
        Catering is included. Catering package includes Hot Dogs, ballpark
        snacks, Coca-Cola Products, Water, Beer, Wine and Seltzers.
      </>
    ),
  },
  {
    title: "When will I receive my tickets?",
    description:
      "Once you checkout and complete your purchase, the tickets and parking passes will be available in the MLB Ballpark app using the same email as your reservation.",
  },
  {
    title: "What happens if the game I purchase is not played?",
    description:
      "If a game is not played, the funds will be added to your account as a credit to be applied towards a suite purchase for the 2027 regular season.",
  },
  {
    title: "What are the 2027 Suite/Party Area Deposits for?",
    description:
      "2027 Suite/Party Area Deposits are valid towards Suites and Party Areas for the 2027 Regular Season and require a minimum of $3,000 spend toward each Suite/Party Area purchase. 2027 Suite/Party Area Deposits are non-refundable and non-combinable.",
  },
  {
    title: "How do I redeem my 2027 Suite/Party Area Deposits?",
    description:
      "After purchasing a 2026 postseason suite or party area, a member of our team will reach out to provide next steps on selecting your game(s) for the 2027 regular season.",
  },
  {
    title: "How do I forward my tickets and parking passes to my guests?",
    description: (
      <>
        Please visit{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="http://www.raysbaseball.com/mobile"
        >
          http://www.raysbaseball.com/mobile
        </Link>{" "}
        for step by step directions on how to access and forward tickets via the
        MLB Ballpark app.
      </>
    ),
  },
];

/**
 * Rendered between the "FAQ" heading and the accordions
 * (`languages.purchaseSeats.faqDisclaimer`, see `FaqSection`), so the suites
 * contact is visible without opening anything.
 */
export const TampaBayRaysFaqDisclaimer = () => (
  <>
    Should you have any questions, or for more information, please contact{" "}
    <Link
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="tel:+17278253406"
    >
      727-825-3406
    </Link>{" "}
    or email{" "}
    <Link
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="mailto:Suites@raysbaseball.com"
    >
      Suites@raysbaseball.com
    </Link>
    .
  </>
);

export const TampaBayRaysCheckoutTerms = () => (
  <>
    In connection with your reservation, you will receive ticket-related
    commercial emails from raysbaseball.com. You may unsubscribe at any time. By
    checking this box you agree to receive additional commercial emails from
    raysbaseball.com and mlb.com. The terms and conditions of your ticket and
    policies applicable to its use are available online at{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/rays/tickets/disclaimer"
    >
      Tampa Bay Rays Ticket Terms & Conditions
    </Link>
    . By clicking the Purchase Tickets button, you agree to accept all Terms and
    Conditions. Once your purchase is complete, a Tampa Bay Rays representative
    will reach out to you directly to connect you with the catering manager.
    Upon completing your purchase, you acknowledge and agree that your credit
    card will be charged. By clicking &quot;Purchase Tickets&quot;, you agree to
    the MLB{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
    >
      Privacy Policy
    </Link>
    ,{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
    >
      Terms of Use
    </Link>
    . Tickets will be made available in the MLB Ballpark mobile application
    only. MLB.com | The Official Site of Major League Baseball and supported
    mobile device required.
  </>
);

export const TampaBayRaysAdditionalDetailsDisclaimerTC = () => (
  <>
    By entering my name, checking the box above and clicking checkout, I hereby
    (i) agree to the Terms of Use (ii) agree that my reservation is complete and
    all sales are final.
  </>
);

export const TampaBayRaysTCListItems = () => (
  <Box>
    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Privacy Policy</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Terms of Use</ListItemText>
    </MenuItem>
  </Box>
);

export const TampaBayRaysRegistrationTerms = () => (
  <>
    By creating an account you agree to be notified regarding your purchase and
    to the{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/616882d2b691e09f75348396_Terms%20%26%20Conditions.pdf"
    >
      Terms & Conditions
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/63c05d80816dac05b5b34ac7_seasonshare%20CCPA%20Privacy%20Policy%20Agreement.pdf"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export function AnalyticsHeader() {
  return (
    <>
      <MLBAdobeScriptHeader />
    </>
  );
}

// ---------------------------------------------------------------------------
// "More Info" modal on the suite inventory cards.
// Shared by tampabayrays_suites and tampabayrays_postseasonsuites.
// ---------------------------------------------------------------------------

type SuiteInfo = {
  title: string;
  description: string;
  amenitiesLabel: string;
  amenities: (string | { text: string; subItems: string[] })[];
};

const suiteTicketsMenu =
  "Menu items included: Hot Dogs, Chicken Tenders, Bavarian Pretzels, Salsa & Guacamole, Bottomless Popcorn, Snack Attack, Salad, Cookies";

const raysSuiteDescription =
  "Experience Rays baseball in comfort from Rays Suites at Tropicana Field. An all-inclusive experience with a variety of vantage points available, Rays Suites are perfect for hosting clients, special occasions, or any family outing.";

const complimentaryMenu =
  "Complimentary menu of food & non-alcoholic beverages (Ability to purchase upgraded menu & alcohol through Levy catering partner.)";

const groupAreaMenu =
  "Menu items included: Hot Dogs, Chicken Tenders, Bavarian Pretzels, Bottomless Popcorn, Snack Attack, Salad, Cookies";

const patioBoxDescription =
  "Bring your group to the ballpark and enjoy complimentary snacks and beverages, including Coca-Cola products, hot dogs, beer and wine from your own private space. Additional catering options are available to complete your outing upon request!";

const patioBoxMenu =
  "Menu Items Includes: Hot Dogs, Pretzels, Chips, Peanuts, Coca-Cola products, beer and wine.";

const baselineSuiteInfo: SuiteInfo = {
  title: "Baseline Suite",
  description: raysSuiteDescription,
  amenitiesLabel: "Rays Baseline Suite amenities include:",
  amenities: [
    "16 Suite Tickets",
    "3 VIP Lot 1 Parking Passes",
    { text: complimentaryMenu, subItems: [suiteTicketsMenu] },
    "Brand new cushioned seats, high-top tables & chairs",
    "Flat Screen TV",
    "Personal Suite Attendant",
  ],
};

const outfieldSuiteInfo: SuiteInfo = {
  title: "Outfield Suite",
  description: raysSuiteDescription,
  amenitiesLabel: "Rays Outfield Suite amenities include:",
  amenities: [
    "12 Suite Tickets (Option of purchasing up to 4 additional tickets)",
    "3 VIP Lot 1 Parking Passes",
    { text: complimentaryMenu, subItems: [suiteTicketsMenu] },
    "Brand new cushioned seats, high-top tables & chairs",
    "Flat Screen TV",
    "Personal Suite Attendant",
  ],
};

const patioBoxInfo = (box: number, amenities: string[]): SuiteInfo => ({
  title: `Patio Box ${box}`,
  description: patioBoxDescription,
  amenitiesLabel: `Patio Box ${box} Amenities Include:`,
  amenities,
});

// Baseline suites hold 16 tickets, Outfield suites 12
// (seatCount in tbl_suite_inventory_mapping).
const baselineSuiteNumbers = [9, 16, 18, 24, 26, 32];
const outfieldSuiteNumbers = [30, 31, 33, 35, 38];

// keyed by the trimmed `suiteName` from tbl_suite_section_mapping
const suiteInfoBySuiteName: Record<string, SuiteInfo> = {
  ...Object.fromEntries(
    baselineSuiteNumbers.map((n) => [`Suite ${n}`, baselineSuiteInfo])
  ),
  ...Object.fromEntries(
    outfieldSuiteNumbers.map((n) => [`Suite ${n}`, outfieldSuiteInfo])
  ),

  "Daiquiri Deck": {
    title: "The Daiquiri Deck",
    description:
      "The Daiquiri Deck offers a game-changing view from the heart of center field. Be on the lookout for home run balls while savoring unique catering options in your exclusive area.",
    amenitiesLabel: "The Daiquiri Deck amenities include:",
    amenities: [
      "30 Tickets",
      { text: complimentaryMenu, subItems: [groupAreaMenu] },
      "High-top tables & chairs with a drink rail",
      "Personal Attendant",
    ],
  },

  "Patio Box 1": patioBoxInfo(1, [
    "10 Tickets",
    patioBoxMenu,
    "Oversized Cushion Seats.",
    "Flat Screen TV.",
  ]),
  "Patio Box 2": patioBoxInfo(2, [
    "12 Tickets",
    patioBoxMenu,
    "Oversized Cushion Seats.",
    "Flat Screen TV.",
  ]),
  "Patio Box 3": patioBoxInfo(3, [
    "22 Tickets",
    patioBoxMenu,
    "High-top tables & Chairs.",
    "Private space in the heart of center field.",
  ]),

  "Seminole Hard Rock 162 Landing": {
    title: "Seminole Hard Rock Tampa 162 Landing",
    description:
      "Enhance company meetings, boost employee morale, reward outstanding achievement, and have a blast doing it! The Seminole Hard Rock Tampa 162 Landing hosts guests in an exclusive, on-field venue located alongside the visiting bullpen!",
    amenitiesLabel: "Seminole Hard Rock Tampa 162 Landing Amenities Include:",
    amenities: [
      "20 Tickets, first come first serve seating.",
      "Shared space with other groups.",
      {
        text: "Complimentary menu of food & non-alcoholic beverages (Cash Bar is available for alcohol options)",
        subItems: [groupAreaMenu],
      },
    ],
  },

  "RIPA Bullpen Box": {
    title: "RIPA Bullpen Box",
    description:
      "RIPA Bullpen Box is the perfect place for team parties, office outings and family events. This exclusive, on-field party area is located alongside the Rays bullpen. If a call is made to the bullpen, you'll be close enough to hear the phone ring and cheer for your favorite Rays pitcher before he enters the game!",
    amenitiesLabel: "RIPA Bullpen Box Amenities Include:",
    amenities: [
      "20 Tickets, first come first serve seating.",
      "Shared space with other groups.",
      {
        text: "Complimentary menu of food & non-alcoholic beverages (Cash Bar is available for alcohol options)",
        subItems: [groupAreaMenu],
      },
    ],
  },
};

export const raysSuiteInfoModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const RaysSuiteInfoModal = () => {
  const modal = raysSuiteInfoModal();
  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

const RaysSuiteInfoContent = ({ info }: { info: SuiteInfo }) => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        {info.title}
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">{info.description}</Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">{info.amenitiesLabel}</Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0, display: "block" }}>
        {info.amenities.map((item) =>
          typeof item === "string" ? (
            <Typography key={item} variant="body2">
              • {item}
            </Typography>
          ) : (
            <React.Fragment key={item.text}>
              <Typography variant="body2">• {item.text}</Typography>
              {item.subItems.map((sub) => (
                <Typography key={sub} variant="body2" sx={{ pl: 2 }}>
                  ◦ {sub}
                </Typography>
              ))}
            </React.Fragment>
          )
        )}
      </ListItem>
    </List>
  </Box>
);

// One button entry for every suite; the modal content is picked by suiteName.
export const raysMoreInfoButton = {
  id: 1,
  label: "More Info",
  suiteNames: Object.keys(suiteInfoBySuiteName),
  onClick: (inventory?: InventoryType) => {
    const suiteName = (inventory as { suiteName?: string })?.suiteName?.trim();
    const info = suiteName ? suiteInfoBySuiteName[suiteName] : undefined;
    if (!info) return;

    raysSuiteInfoModal.getState().set({
      isOpen: true,
      title: "More Info",
      content: <RaysSuiteInfoContent info={info} />,
    });
  },
};

// Carousel photos for the group areas, keyed by `mapSectionName`.
// These areas have no node on the venue map, so they are also listed in
// `raysGroupAreaMapSectionNames` to keep the 3D slide out of the carousel.
export const raysGroupAreaImages: Record<string, string[]> = {
  DaiquiriDeck: [
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Daiquiri%20Deck/The%20Daiquiri%20Deck%20-%201.jpg",
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Daiquiri%20Deck/The%20Daiquiri%20Deck%20-%202.jpg",
  ],
  PatioBox1: [
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Patio%20Boxes/Patio%20Box%201%20%2810%20tix%29%20-%20NEW.jpg",
  ],
  PatioBox2: [
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Patio%20Boxes/Patio%20Box%202%20%2812%20tix%29%20-%20NEW.jpg",
  ],
  PatioBox3: [
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Patio%20Boxes/Patio%20Box%203%20%2822%20Tix%29%20Photo%201.webp",
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Patio%20Boxes/Patio%20Box%203%20%2822%20tix%29%20Photo%202.webp",
  ],
  RIPABullpenBox: [
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/RIPA%20Bullpen%20Box/RIPA%20Bullpen%20Box%20-%201.jpg",
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/RIPA%20Bullpen%20Box/RIPA%20Bullpen%20Box%20-%202.jpg",
  ],
  SeminoleHardRock162Landing: [
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Seminole%20Hard%20Rock%20162%20Landing/Seminole%20Hard%20Rock%20Tampa%20162%20Landing%20%281%29.jpg",
    "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Seminole%20Hard%20Rock%20162%20Landing/Seminole%20Hard%20Rock%20Tampa%20162%20Landing%20%282%29.jpg",
  ],
};

export const raysGroupAreaMapSectionNames = Object.keys(raysGroupAreaImages);
